import { countStatusDefault } from '@/modules/move/entities/constants/moveRow';
import { MOVE_ACTION, MOVE_ROW_BENEFICIARY_STATUS } from '@/modules/move/entities/enums/move';
import { MoveNormalized } from '@/modules/move/entities/interfaces/move';
import { MoveRowService } from '@/modules/move/services/moveRowService';
import { BeneficiaryStatusNumbers } from '@/modules/move/views/countBeneficiaryStatus/beneficiaryStatusNumbers';
import { useRequestReadOne } from '@/shared/hooks/useRequestData/useRequestReadOne';
import React, { useMemo } from 'react';
import { Button, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface CountBeneficiaryStatusProps {
  type: MOVE_ACTION;
  move: MoveNormalized;
}

export const CountBeneficiaryStatus: React.FC<CountBeneficiaryStatusProps> = ({ type, move }) => {
  const {
    data: statuses = countStatusDefault,
    loading,
    loadData,
  } = useRequestReadOne({
    source: () => {
      return new MoveRowService().getCountRowsByBeneficiaryStatus({ fileId: move.id });
    },
  });

  const countInitialStatus =
    statuses[MOVE_ROW_BENEFICIARY_STATUS.VALIDATED] +
    statuses[MOVE_ROW_BENEFICIARY_STATUS.NOT_INITIALIZED];
  const sumTotal = Object.values(statuses).reduce((a, b) => a + b, 0);
  const actionText = {
    [MOVE_ACTION.CREATION]: 'Inclusões',
    [MOVE_ACTION.REMOVAL]: 'Exclusões',
    [MOVE_ACTION.UPDATE]: 'Atualizações',
  }[type];

  const description = useMemo(() => {
    if (sumTotal === 0) {
      return;
    }

    if (
      statuses[MOVE_ROW_BENEFICIARY_STATUS.PROCESSED_WITH_ERROR] > 0 ||
      statuses[MOVE_ROW_BENEFICIARY_STATUS.VALIDATED_WITH_ERROR] > 0
    ) {
      return (
        <Grid gap={4} alignItems='center'>
          <Text
            value='Erros e/ou criticas foram identificados na sua movimentação.'
            color='danger'
            size='medium'
          />
          <Button
            solo
            label='Ver detalhes'
            href={`/companies/${move.companyId}/beneficiaries/moves/${move.id}/details`}
          />
        </Grid>
      );
    }

    if (statuses[MOVE_ROW_BENEFICIARY_STATUS.PROCESSED] === sumTotal) {
      return <Text value='Sua movimentação foi finalizada com sucesso.' size='medium' />;
    }

    if (countInitialStatus === sumTotal) {
      return <Text value='Em breve, os status das inclusões estarão disponíveis.' size='medium' />;
    }
  }, [countInitialStatus, move.companyId, move.id, statuses, sumTotal]);

  return (
    <Grid column padding='16px 10px 0 10px' gap={16}>
      <Grid gap={40} alignItems='center'>
        <Text title value={`${actionText}(${sumTotal})`} size='minor' />
        {description}
      </Grid>

      <BeneficiaryStatusNumbers loading={loading} statuses={statuses} loadData={loadData} />
    </Grid>
  );
};
